'use client';

/**
 * Registers the XOFFI Core service worker (public/sw.js, built from app/sw.js by
 * serwist) and drives a safe update flow:
 *
 *  - Instant paint: the SW serves the last-cached shell (stale-while-revalidate),
 *    then refreshes in the background.
 *  - Background version check: registration.update() on an hourly interval and
 *    whenever the tab regains focus. A new build → new sw.js content → the browser
 *    installs the update and parks it in `waiting`.
 *  - SILENT update (no toast): a new build is detected internally. It is NEVER
 *    applied mid-interaction, but at the next page load or at the next route change
 *    (pathname change) — then the waiting worker is activated (SKIP_WAITING) and the
 *    page is reloaded once, so the user lands on the new version without a prompt.
 *  - Other tabs: when another tab activated the new worker, this tab reloads on ITS
 *    next route change (its JS is old but the SW/cache is already new).
 *  - Loop-safe: auto-reload at most once per 30s (sessionStorage guard) and
 *    first-install controllerchange (clientsClaim) never reloads.
 *
 * Only active in production. The core dev box runs `next dev --turbopack` where
 * serwist is disabled and no real sw.js exists — registering there would 404 /
 * serve the kill-switch placeholder.
 */
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

const SW_URL = '/sw.js';
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // hourly

const RELOAD_GUARD_KEY = 'xoffi-sw-auto-reload-at';
const RELOAD_GUARD_MS = 30 * 1000;
const NAV_UPDATE_THROTTLE_MS = 60 * 1000;

export function ServiceWorkerProvider() {
  const pathname = usePathname();
  const registrationRef = useRef(null);
  const reloadInitiatedRef = useRef(false);
  const staleTabRef = useRef(false); // another tab activated a newer SW
  const lastUpdateCheckRef = useRef(0);
  const firstPathRef = useRef(true);

  // Aktiviert einen wartenden Worker (falls vorhanden) und lädt danach einmal neu.
  // Gibt true zurück, wenn ein Update angestoßen wurde.
  const applyPendingUpdate = () => {
    if (staleTabRef.current) {
      staleTabRef.current = false;
      if (reloadAllowed()) {
        markReload();
        window.location.reload();
        return true;
      }
      return false;
    }
    const waiting = registrationRef.current?.waiting;
    if (!waiting || !navigator.serviceWorker.controller) return false;
    if (!reloadAllowed()) return false;
    markReload();
    reloadInitiatedRef.current = true;
    waiting.postMessage({ type: 'SKIP_WAITING' });
    return true;
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    // react.xoffi.com = next dev. Der Serwist-SW (/sw.js) darf dort nicht
    // registriert werden (HMR/Caching). Ohne JEDEN SW bleibt „Zum Home“ ein
    // Lesezeichen MIT URL-Leiste. Deshalb nur hier der Mini-SW /sw-pwa.js.
    if (process.env.NODE_ENV !== 'production') {
      navigator.serviceWorker
        // updateViaCache:'none' — sonst darf der Browser das SW-Skript selbst
        // bis zu 24h aus dem HTTP-Cache bedienen, und eine Korrektur am Worker
        // käme auf dem Gerät schlicht nicht an.
        .register('/sw-pwa.js', { scope: '/', updateViaCache: 'none' })
        .catch((err) => console.warn('[SW] pwa-install register failed:', err?.message));
      return undefined;
    }

    let intervalId;
    // Gab es beim Seitenstart schon einen steuernden SW? Nur dann ist ein späterer
    // controllerchange ein UPDATE (und nicht die Erstinstallation via clientsClaim).
    const hadController = !!navigator.serviceWorker.controller;

    navigator.serviceWorker
      .register(SW_URL, { scope: '/', updateViaCache: 'none' })
      .then((reg) => {
        registrationRef.current = reg;
        // Ein in einer früheren Sitzung/Tab installierter Worker wartet schon:
        // jetzt (frühester Zeitpunkt, der User hat noch nichts getan) aktivieren.
        if (reg.waiting && navigator.serviceWorker.controller) {
          applyPendingUpdate();
        }
        intervalId = setInterval(() => reg.update().catch(() => {}), UPDATE_CHECK_INTERVAL_MS);
      })
      .catch((err) => console.warn('[SW] register failed:', err?.message));

    const onVisible = () => {
      if (document.visibilityState === 'visible' && registrationRef.current) {
        registrationRef.current.update().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    // Der neue SW hat die Kontrolle übernommen. Neu laden nur, wenn WIR das
    // angestoßen haben. Hat ein anderer Tab aktiviert, merken wir uns das und
    // laden beim nächsten Routenwechsel neu.
    const onControllerChange = () => {
      if (reloadInitiatedRef.current) {
        reloadInitiatedRef.current = false;
        window.location.reload();
      } else if (hadController) {
        staleTabRef.current = true;
      }
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  // Routenwechsel = "nächstes Laden einer Seite": wartendes Update jetzt anwenden,
  // sonst (gedrosselt) im Hintergrund nach einer neuen Version suchen.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    if (firstPathRef.current) {
      firstPathRef.current = false;
      return;
    }
    if (applyPendingUpdate()) return;
    const now = Date.now();
    if (registrationRef.current && now - lastUpdateCheckRef.current > NAV_UPDATE_THROTTLE_MS) {
      lastUpdateCheckRef.current = now;
      registrationRef.current.update().catch(() => {});
    }
  }, [pathname]);

  return null;
}

// Schleifenschutz: höchstens ein automatischer Reload je 30s.
function reloadAllowed() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    return Date.now() - last > RELOAD_GUARD_MS;
  } catch {
    return true;
  }
}

function markReload() {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    /* sessionStorage gesperrt — Guard entfällt */
  }
}
